<template>
  <div>
    <VideoInfo :videoFile="uploadedVideo.file" @dataExtracted="onDataExtracted" @error="handleThumbnailExtractError" />
    <FilestackS3
      ref="referenceFilestackS3"
      :s3path="s3BucketPath"
      :acceptedTypes="['video/mp4']"
      :maxSize="maxSize"
      :disableStorageKey="true"
      :customFilePreprocess="true"
      :customFilePreprocessFun="videoFileUploadPreProcessor"
      @init="$emit('init')"
      @close="$emit('close')"
      @fileuploaded="files => onVideoFileUpload(files)"
    >
      <slot />
    </FilestackS3>
  </div>
</template>

<script lang="ts">
import { SeriesResponse } from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';

import { GET_VIDEO_PATH, processErrorParent } from './utils';
import VideoInfo from './VideoInfo.vue';
import { uploadVideoAsset, VideoAsset } from '../videoLibrary/videoLibraryService';

import FilestackS3 from '@/components/core/Filestack/FilestackS3.vue';
import { uuidGen } from '@/components/core/Filestack/utils';
import { UPLOAD_PREFIX } from '@/constants';

export default defineComponent({
  name: 'UploadVideo',
  components: { FilestackS3, VideoInfo },
  props: {
    maxSize: {
      type: Number,
      default: 5000 * 1024 * 1024
    }
  },
  data() {
    return {
      uploadedVideo: {
        id: undefined as string | undefined,
        name: undefined as string | undefined,
        thumbnailFile: undefined as Blob | undefined,
        file: undefined as File | undefined,
        duration: undefined as number | undefined
      }
    };
  },
  computed: {
    ...mapGetters('org', { getCurrentOrg: 'getCurrentOrg' }),
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    }),
    ...mapGetters('studioTemplateList', {
      template: 'getCurrentTemplate'
    }),
    ...mapGetters('studioSeriesList', {
      getCurrentSeriesById: 'getCurrentSeriesById'
    }),
    series(): SeriesResponse | null {
      if (!this.$route.params.seriesId) {
        return null;
      }

      return this.getCurrentSeriesById(this.$route.params.seriesId);
    },
    isRepeatingEvent(): boolean {
      return this.series?.type === 'repeating';
    },
    s3BucketPath(): string {
      return this.getCurrentOrg.id ? GET_VIDEO_PATH(this.getCurrentOrg.id) : '';
    }
  },
  methods: {
    ...mapActions('studioToast', ['triggerToast']),
    extractIdFromFilename(filename: string): string | null {
      if (!filename) {
        return null;
      }
      return filename.split('_')[0] || null;
    },
    videoFileUploadPreProcessor(file) {
      const id = uuidGen();
      this.uploadedVideo.id = id;
      this.uploadedVideo.file = file?.originalFile;

      return {
        ...file,
        name: `${id}_${UPLOAD_PREFIX}.mp4`
      };
    },
    onVideoFileUpload(files) {
      const uploadedFile = files?.filesUploaded?.[0];
      const uploadedFileName = uploadedFile?.originalFile?.name;
      const renamedFileName = uploadedFile?.name || uploadedFile?.filename;
      const fileId = this.extractIdFromFilename(renamedFileName);

      if (
        !files ||
        !files.filesUploaded ||
        !files.filesUploaded[0] ||
        !files.filesUploaded[0].originalFile ||
        !uploadedFileName ||
        !fileId ||
        !this.uploadedVideo.thumbnailFile
      ) {
        logger.error('UploadVideo', 'Invalid file upload', {
          files,
          uploadedVideo: this.uploadedVideo,
          extractedId: fileId
        });
        this.handleError();
        return;
      }

      const formData = new FormData();

      formData.append('id', fileId);
      formData.append('name', uploadedFileName);
      formData.append('organization', this.getCurrentOrg.id);
      formData.append('asset_type', 'VIDEO');
      formData.append('uploaded', 'true');
      formData.append('duration', String(this.uploadedVideo.duration));
      formData.append('thumbnail_to_upload', this.uploadedVideo.thumbnailFile);

      let workspaceId = '';
      if (this.$route.params.eventId || this.isRepeatingEvent) {
        workspaceId = this.event?.workspace?.id;
      } else if (this.$route.params.templateId) {
        workspaceId = this.template?.workspace?.id;
      }

      if (workspaceId) {
        formData.append('workspace', workspaceId);
      }

      uploadVideoAsset(formData)
        .then((videoAsset: VideoAsset) => {
          this.$emit('success', videoAsset);
        })
        .catch((error: Error) => {
          logger.error('UploadVideo', 'File upload failed', {
            error: error.message
          });
          this.$emit('failure');
          this.handleError(processErrorParent(error.message));
        });
    },
    handleError(errorMessage?: string) {
      this.triggerToast({
        mode: 'error',
        msg: errorMessage || 'File upload failed'
      });
    },
    onDataExtracted(data) {
      this.uploadedVideo.thumbnailFile = data.thumbnailFile;
      this.uploadedVideo.duration = data.duration;
    },
    handleThumbnailExtractError(error) {
      (this.$refs.referenceFilestackS3 as any).closePicker();
      logger.error('UploadVideo', 'Thumbnail extract error', error);
      this.handleError(processErrorParent(error.message));
    }
  }
});
</script>
